/**
 * TUVI - Módulo de Pagos
 * Dashboard centralizado para gestión de pagos a proveedores
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import { UPLOAD_URL } from '../../config';
import ModalRegistrarPago from './ModalRegistrarPago';
import AjustesCartera from './AjustesCartera';
import XLSX from 'xlsx-js-style';
import './Pagos.css';

const Pagos = () => {
  const { success, error } = useNotification();
  const [loading, setLoading] = useState(true);
  const [estadisticas, setEstadisticas] = useState(null);
  const [facturasPendientes, setFacturasPendientes] = useState([]);
  const [historialPagos, setHistorialPagos] = useState([]);
  const [retefuentesPendientes, setRetefuentesPendientes] = useState([]);
  const [retefuenteEstadisticas, setRetefuenteEstadisticas] = useState({ total_facturas: 0, total_retefuente: 0, total_proveedores: 0 });
  const [retefuentesSeleccionadas, setRetefuentesSeleccionadas] = useState([]);
  const [mostrarModalRetefuente, setMostrarModalRetefuente] = useState(false);
  const [historialRetefuente, setHistorialRetefuente] = useState([]);
  const [mostrarHistorialRet, setMostrarHistorialRet] = useState(false);
  const [formRetefuente, setFormRetefuente] = useState({
    fecha_pago: new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' }),
    periodo: '',
    metodo_pago: 'transferencia',
    referencia: '',
    banco: '',
    notas: ''
  });
  const [guardandoRetefuente, setGuardandoRetefuente] = useState(false);
  const [vista, setVista] = useState('dashboard'); // dashboard, pendientes, historial, retefuente
  // Default: último mes (Colombia timezone)
  const hoyCO = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
  const unMesAtras = new Date();
  unMesAtras.setMonth(unMesAtras.getMonth() - 1);
  const unMesAtrasCO = unMesAtras.toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
  const [filtros, setFiltros] = useState({
    proveedor_id: '',
    proyecto_id: '',
    estado: '',
    ordenar: 'vencimiento',
    busqueda: '',
    fecha_desde: unMesAtrasCO,
    fecha_hasta: hoyCO
  });
  const [filtrosOpciones, setFiltrosOpciones] = useState({ proveedores: [], proyectos: [] });
  const [mostrarModalPago, setMostrarModalPago] = useState(false);
  const [facturasSeleccionadas, setFacturasSeleccionadas] = useState([]);
  const [gruposExpandidos, setGruposExpandidos] = useState({});
  const [gruposHistorialExpandidos, setGruposHistorialExpandidos] = useState({});
  const [eventosExpandidos, setEventosExpandidos] = useState({});

  useEffect(() => {
    cargarEstadisticas();
  }, []);

  useEffect(() => {
    if (vista === 'pendientes') {
      cargarFacturasPendientes();
    } else if (vista === 'historial') {
      cargarHistorialPagos();
    } else if (vista === 'retefuente') {
      cargarRetefuentesPendientes();
    }
  }, [vista, filtros]);

  const cargarEstadisticas = async () => {
    try {
      setLoading(true);
      const data = await client.query(`{ estadisticasPagos { resumen { facturas_pendientes monto_pendiente facturas_vencidas monto_vencido pagos_mes pagado_mes } proyeccion { dias_7_monto dias_7_facturas dias_15_monto dias_15_facturas dias_30_monto dias_30_facturas } por_proveedor { proveedor_id proveedor_nombre nit total_facturas saldo_total facturas_vencidas } } }`);
      if (data.estadisticasPagos) {
        setEstadisticas({
          success: true,
          resumen: data.estadisticasPagos.resumen,
          proyeccion: {
            dias_7: { monto: data.estadisticasPagos.proyeccion.dias_7_monto, facturas: data.estadisticasPagos.proyeccion.dias_7_facturas },
            dias_15: { monto: data.estadisticasPagos.proyeccion.dias_15_monto, facturas: data.estadisticasPagos.proyeccion.dias_15_facturas },
            dias_30: { monto: data.estadisticasPagos.proyeccion.dias_30_monto, facturas: data.estadisticasPagos.proyeccion.dias_30_facturas }
          },
          alertas: { vencidas: [], por_vencer_7_dias: [], por_vencer_15_dias: [] },
          por_proveedor: data.estadisticasPagos.por_proveedor
        });
      }
    } catch (err) {
      console.error('Error al cargar estadísticas:', err);
      error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  const cargarFacturasPendientes = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filtros.proveedor_id) params.append('proveedor_id', filtros.proveedor_id);
      if (filtros.proyecto_id) params.append('proyecto_id', filtros.proyecto_id);
      if (filtros.estado) params.append('estado', filtros.estado);
      if (filtros.ordenar) params.append('ordenar', filtros.ordenar);
      if (filtros.busqueda) params.append('busqueda', filtros.busqueda);

      const vars = {};
      if (filtros.proveedor_id) vars.proveedor_id = parseInt(filtros.proveedor_id);
      if (filtros.proyecto_id) vars.proyecto_id = parseInt(filtros.proyecto_id);
      if (filtros.estado) vars.estado = filtros.estado;
      if (filtros.ordenar) vars.ordenar = filtros.ordenar;
      if (filtros.busqueda) vars.busqueda = filtros.busqueda;

      const data = await client.query(`query($proveedor_id: Int, $proyecto_id: Int, $estado: String, $ordenar: String, $busqueda: String) { facturasPendientes(proveedor_id: $proveedor_id, proyecto_id: $proyecto_id, estado: $estado, ordenar: $ordenar, busqueda: $busqueda) { facturas { id proveedor_id proyecto_id numero_factura fecha_emision fecha_vencimiento monto monto_pagado saldo_pendiente estado proveedor_nombre proveedor_nit proyecto_nombre nombre_cliente apartamento archivo_factura urgencia dias_vencido dias_para_vencer total_retenciones saldo_real prov_porcentaje_retencion prov_base_retencion prov_tipo_retencion retencion_fuente retencion_iva otras_retenciones porcentaje_iva subtotal retenciones { retencion_id tipo_retencion_id porcentaje_retencion base_retencion base_retencion_texto es_principal tipo_retencion_codigo tipo_retencion_nombre } } estadisticas { total_facturas total_pendiente vencidas proximas } } }`, vars);

      setFacturasPendientes(data.facturasPendientes.facturas || []);
      setFiltrosOpciones(prev => ({ ...prev }));
    } catch (err) {
      console.error('Error al cargar facturas:', err);
    } finally {
      setLoading(false);
    }
  };

  const cargarHistorialPagos = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filtros.proveedor_id) params.append('proveedor_id', filtros.proveedor_id);
      if (filtros.busqueda) params.append('busqueda', filtros.busqueda);

      const hVars = { limite: 500 };
      if (filtros.proveedor_id) hVars.proveedor_id = parseInt(filtros.proveedor_id);
      if (filtros.busqueda) hVars.busqueda = filtros.busqueda;
      if (filtros.fecha_desde) hVars.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) hVars.fecha_hasta = filtros.fecha_hasta;

      const data = await client.query(`query($proveedor_id: Int, $busqueda: String, $fecha_desde: String, $fecha_hasta: String, $limite: Int) { historialPagos(proveedor_id: $proveedor_id, busqueda: $busqueda, fecha_desde: $fecha_desde, fecha_hasta: $fecha_hasta, limite: $limite) { pagos { id factura_id monto_pago fecha_pago metodo_pago referencia banco notas comprobante_archivo comprobante_ruta numero_factura factura_monto factura_saldo factura_estado factura_archivo proveedor_id proveedor_nombre proveedor_nit proyecto_id proyecto_nombre nombre_cliente } paginacion { total } estadisticas { total_pagos monto_total } } }`, hVars);

      setHistorialPagos(data.historialPagos.pagos || []);
    } catch (err) {
      console.error('Error al cargar historial:', err);
    } finally {
      setLoading(false);
    }
  };

  const cargarRetefuentesPendientes = async () => {
    try {
      setLoading(true);
      const vars = {};
      if (filtros.proveedor_id) vars.proveedor_id = parseInt(filtros.proveedor_id);
      if (filtros.busqueda) vars.busqueda = filtros.busqueda;

      const data = await client.query(
        `query($proveedor_id: Int, $busqueda: String) { retefuentesPendientes(proveedor_id: $proveedor_id, busqueda: $busqueda) { facturas { id proveedor_id proyecto_id numero_factura fecha_emision fecha_vencimiento monto monto_pagado saldo_pendiente estado proveedor_nombre proveedor_nit proyecto_nombre nombre_cliente apartamento retencion_fuente retencion_iva otras_retenciones subtotal porcentaje_iva prov_porcentaje_retencion prov_tipo_retencion } estadisticas { total_facturas total_retefuente total_proveedores } } }`,
        vars
      );
      setRetefuentesPendientes(data.retefuentesPendientes.facturas || []);
      setRetefuenteEstadisticas(data.retefuentesPendientes.estadisticas || { total_facturas: 0, total_retefuente: 0, total_proveedores: 0 });
    } catch (err) {
      console.error('Error al cargar retefuentes:', err);
      error('Error al cargar retefuentes pendientes');
    } finally {
      setLoading(false);
    }
  };

  const cargarHistorialRetefuente = async () => {
    try {
      const vars = {};
      if (filtros.fecha_desde) vars.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) vars.fecha_hasta = filtros.fecha_hasta;
      const data = await client.query(
        `query($fecha_desde: String, $fecha_hasta: String) { historialPagosRetefuente(fecha_desde: $fecha_desde, fecha_hasta: $fecha_hasta) { id fecha_pago monto_total periodo metodo_pago referencia banco comprobante_archivo notas total_facturas facturas { id numero_factura proveedor_nombre proveedor_nit proyecto_nombre apartamento retencion_fuente } } }`,
        vars
      );
      setHistorialRetefuente(data.historialPagosRetefuente || []);
    } catch (err) {
      console.error('Error al cargar historial retefuente:', err);
    }
  };

  const toggleSeleccionRetefuente = (factura) => {
    setRetefuentesSeleccionadas(prev => {
      const existe = prev.find(f => f.id === factura.id);
      if (existe) return prev.filter(f => f.id !== factura.id);
      return [...prev, factura];
    });
  };

  const seleccionarTodasRetefuentes = () => {
    if (retefuentesSeleccionadas.length === retefuentesPendientes.length) {
      setRetefuentesSeleccionadas([]);
    } else {
      setRetefuentesSeleccionadas([...retefuentesPendientes]);
    }
  };

  const totalRetefuenteSeleccionado = retefuentesSeleccionadas.reduce(
    (s, f) => s + parseFloat(f.retencion_fuente || 0) + parseFloat(f.retencion_iva || 0), 0
  );

  const procesarPagoRetefuente = async () => {
    if (retefuentesSeleccionadas.length === 0) {
      error('Debe seleccionar al menos una factura');
      return;
    }
    if (!formRetefuente.fecha_pago) {
      error('La fecha de pago es requerida');
      return;
    }
    try {
      setGuardandoRetefuente(true);
      const data = await client.mutate(
        `mutation($input: PagarRetefuenteInput!) { pagarRetefuentes(input: $input) { success message pago_id monto_total facturas_procesadas } }`,
        {
          input: {
            facturas_ids: retefuentesSeleccionadas.map(f => f.id),
            fecha_pago: formRetefuente.fecha_pago,
            periodo: formRetefuente.periodo || null,
            metodo_pago: formRetefuente.metodo_pago || null,
            referencia: formRetefuente.referencia || null,
            banco: formRetefuente.banco || null,
            notas: formRetefuente.notas || null,
          }
        }
      );
      if (data.pagarRetefuentes.success) {
        success(data.pagarRetefuentes.message);
        setMostrarModalRetefuente(false);
        setRetefuentesSeleccionadas([]);
        setFormRetefuente({
          fecha_pago: new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' }),
          periodo: '',
          metodo_pago: 'transferencia',
          referencia: '',
          banco: '',
          notas: ''
        });
        cargarRetefuentesPendientes();
        cargarEstadisticas();
      } else {
        error(data.pagarRetefuentes.message || 'Error al pagar retefuente');
      }
    } catch (err) {
      console.error('Error al pagar retefuente:', err);
      error('Error al procesar el pago: ' + (err.message || 'desconocido'));
    } finally {
      setGuardandoRetefuente(false);
    }
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  const toggleSeleccionFactura = (factura) => {
    setFacturasSeleccionadas(prev => {
      const existe = prev.find(f => f.id === factura.id);
      if (existe) {
        return prev.filter(f => f.id !== factura.id);
      }
      // Solo permitir facturas del mismo proveedor
      if (prev.length > 0 && prev[0].proveedor_id !== factura.proveedor_id) {
        error('Solo puedes seleccionar facturas del mismo proveedor');
        return prev;
      }
      return [...prev, factura];
    });
  };

  const seleccionarTodas = () => {
    if (facturasSeleccionadas.length === facturasPendientes.length) {
      setFacturasSeleccionadas([]);
    } else {
      // Solo del primer proveedor seleccionado o del filtro actual
      const proveedorId = filtros.proveedor_id || (facturasPendientes[0]?.proveedor_id);
      if (proveedorId) {
        setFacturasSeleccionadas(
          facturasPendientes.filter(f => f.proveedor_id === parseInt(proveedorId))
        );
      }
    }
  };

  const handlePagoRegistrado = () => {
    setMostrarModalPago(false);
    setFacturasSeleccionadas([]);
    cargarEstadisticas();
    if (vista === 'pendientes') cargarFacturasPendientes();
    if (vista === 'historial') cargarHistorialPagos();
    success('Pago registrado exitosamente');
  };

  const formatMoney = (valor) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(valor || 0);
  };

  const formatDate = (fecha) => {
    if (!fecha) return '-';
    // Anclar a mediodía Colombia para que no se corra al día anterior por timezone
    const f = String(fecha);
    const dt = new Date(f.length === 10 ? f + 'T12:00:00-05:00' : f);
    return dt.toLocaleDateString('es-CO', {
      timeZone: 'America/Bogota',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getUrgenciaClass = (factura) => {
    if (factura.urgencia === 'vencida') return 'urgencia-vencida';
    if (factura.urgencia === 'proxima') return 'urgencia-proxima';
    return '';
  };

  const getUrgenciaText = (factura) => {
    if (factura.urgencia === 'vencida') {
      return `Vencida hace ${Math.abs(factura.dias_vencido)} días`;
    }
    if (factura.urgencia === 'proxima') {
      return `Vence en ${factura.dias_para_vencer} días`;
    }
    return '';
  };

  const toggleGrupo = (proveedorId) => {
    setGruposExpandidos(prev => ({ ...prev, [proveedorId]: !prev[proveedorId] }));
  };

  const editarFechaPago = async (pagoId, fechaActual) => {
    const fechaHoy = fechaActual || new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
    const nuevaFecha = window.prompt('Nueva fecha del pago (formato YYYY-MM-DD):', fechaHoy);
    if (!nuevaFecha) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(nuevaFecha)) {
      error('Formato inválido. Use YYYY-MM-DD (ej: 2026-04-17)');
      return;
    }
    try {
      const data = await client.mutate(
        `mutation($id: Int!, $fecha_pago: String!) { editarFechaPago(id: $id, fecha_pago: $fecha_pago) { success message } }`,
        { id: pagoId, fecha_pago: nuevaFecha }
      );
      if (data.editarFechaPago.success) {
        success('Fecha actualizada');
        cargarHistorialPagos();
      } else {
        error(data.editarFechaPago.message || 'Error al actualizar');
      }
    } catch (err) {
      error('Error al actualizar fecha: ' + err.message);
    }
  };

  // Agrupar facturas por proveedor
  const facturasAgrupadas = useMemo(() => {
    const grupos = {};
    // Filtrar por rango de fecha de vencimiento
    let listaFiltrada = facturasPendientes;
    if (filtros.venc_desde) listaFiltrada = listaFiltrada.filter(f => f.fecha_vencimiento && f.fecha_vencimiento >= filtros.venc_desde);
    if (filtros.venc_hasta) listaFiltrada = listaFiltrada.filter(f => f.fecha_vencimiento && f.fecha_vencimiento <= filtros.venc_hasta);
    listaFiltrada.forEach(f => {
      const key = f.proveedor_id;
      if (!grupos[key]) {
        grupos[key] = {
          proveedor_id: f.proveedor_id,
          proveedor_nombre: f.proveedor_nombre,
          proveedor_nit: f.proveedor_nit,
          facturas: [],
          total_monto: 0,
          total_saldo: 0
        };
      }
      grupos[key].facturas.push(f);
      grupos[key].total_monto += parseFloat(f.monto || 0);
      grupos[key].total_saldo += parseFloat(f.saldo_real || f.saldo_pendiente || 0);
    });
    // Ordenar facturas dentro de cada proveedor por # factura (menor a mayor)
    Object.values(grupos).forEach(g => {
      g.facturas.sort((a, b) => {
        const numA = parseInt(a.numero_factura) || 0;
        const numB = parseInt(b.numero_factura) || 0;
        return numA - numB;
      });
    });
    return Object.values(grupos).sort((a, b) => b.total_saldo - a.total_saldo);
  }, [facturasPendientes, filtros.venc_desde, filtros.venc_hasta]);

  // Exportar el HISTORIAL DE PAGOS (lo que está en el filtro) a Excel con formato.
  const exportarHistorialExcel = async () => {
    if (historialPagos.length === 0) { error('No hay pagos para exportar'); return; }

    // Links de descarga de los comprobantes. Llevan un token propio del archivo,
    // con vencimiento, para que el Excel se pueda compartir sin repartir la sesion.
    let mapaLinks = new Map();
    let diasValidez = 30;
    try {
      const d = await client.query(
        `query($d: String, $h: String) { linksComprobantesPagos(fecha_desde: $d, fecha_hasta: $h) {
          dias_validez links { bucket clave url } } }`,
        { d: filtros.fecha_desde || null, h: filtros.fecha_hasta || null });
      const res = d.linksComprobantesPagos;
      diasValidez = res?.dias_validez || 30;
      (res?.links || []).forEach(l => { if (l.url) mapaLinks.set(`${l.bucket}||${l.clave}`, l.url); });
    } catch (e) {
      console.error('No se pudieron generar los links de comprobantes:', e);
      error('El Excel saldrá sin los links de los comprobantes');
    }
    const linkPago = (p) =>
      mapaLinks.get(`comprobantes||${p.comprobante_ruta}`) ||
      mapaLinks.get(`comprobantes||${p.comprobante_archivo}`) || '';
    const linkFactura = (p) => mapaLinks.get(`facturas||${p.factura_archivo}`) || '';

    const wb = XLSX.utils.book_new();
    const coloresProveedor = [
      { bg: '1A2432', fg: 'FFFFFF' }, { bg: '2E7D32', fg: 'FFFFFF' }, { bg: '1565C0', fg: 'FFFFFF' },
      { bg: '6A1B9A', fg: 'FFFFFF' }, { bg: 'C62828', fg: 'FFFFFF' }, { bg: 'E65100', fg: 'FFFFFF' },
      { bg: '00695C', fg: 'FFFFFF' }, { bg: '4527A0', fg: 'FFFFFF' }, { bg: '283593', fg: 'FFFFFF' },
      { bg: '33691E', fg: 'FFFFFF' }, { bg: 'BF360C', fg: 'FFFFFF' }, { bg: '01579B', fg: 'FFFFFF' },
    ];
    const estiloTitulo = { font: { bold: true, sz: 16, color: { rgb: '1A2432' } }, alignment: { horizontal: 'left', vertical: 'center' } };
    const estiloSubtitulo = { font: { sz: 11, color: { rgb: '555555' } }, alignment: { horizontal: 'left' } };
    const estiloResumen = { font: { bold: true, sz: 12, color: { rgb: '2E7D32' } }, alignment: { horizontal: 'left' } };
    const estiloHeaderCol = { font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '37474F' } }, alignment: { horizontal: 'center', vertical: 'center', wrapText: true }, border: { bottom: { style: 'thin', color: { rgb: '000000' } } } };
    const estiloCelda = { font: { sz: 10 }, alignment: { vertical: 'center' }, border: { bottom: { style: 'hair', color: { rgb: 'DDDDDD' } } } };
    const estiloMoney = { ...estiloCelda, numFmt: '#,##0', alignment: { horizontal: 'right', vertical: 'center' } };
    const estiloEvento = { font: { bold: true, sz: 10, color: { rgb: '1A2432' } }, fill: { fgColor: { rgb: 'E8F5E9' } }, alignment: { horizontal: 'left', vertical: 'center' } };
    const estiloLink = { font: { sz: 10, color: { rgb: '1565C0' }, underline: true }, alignment: { horizontal: 'center', vertical: 'center' } };
    const hipervinculos = [];

    // Agrupar por proveedor (igual que en la vista)
    const grupos = {};
    historialPagos.forEach(p => {
      const key = p.proveedor_id || 'sin';
      if (!grupos[key]) grupos[key] = { nombre: p.proveedor_nombre || 'Sin proveedor', nit: p.proveedor_nit, pagos: [], total: 0 };
      grupos[key].pagos.push(p);
      grupos[key].total += (p.monto_pago || 0);
    });
    const gruposArr = Object.values(grupos).sort((a, b) => b.total - a.total);
    const totalGeneral = gruposArr.reduce((s, g) => s + g.total, 0);
    const evKey = (p) => p.comprobante_ruta || `${p.fecha_pago || ''}__${p.referencia || ''}__${p.metodo_pago || ''}`;

    // ===== HOJA 1: Detalle =====
    const wsData = []; const merges = []; const cellStyles = {};
    const addStyle = (row, col, style) => { cellStyles[XLSX.utils.encode_cell({ r: row, c: col })] = style; };
    const NCOL = 10; // 11 columnas (0..10)

    wsData.push(['TUVI - HISTORIAL DE PAGOS A PROVEEDORES']);
    merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: NCOL } }); addStyle(0, 0, estiloTitulo);
    wsData.push([`Generado: ${new Date().toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`]);
    merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: NCOL } }); addStyle(1, 0, estiloSubtitulo);
    wsData.push([`Rango: ${filtros.fecha_desde || '—'} a ${filtros.fecha_hasta || '—'}${filtros.busqueda ? `   |   Búsqueda: "${filtros.busqueda}"` : ''}`]);
    merges.push({ s: { r: 2, c: 0 }, e: { r: 2, c: NCOL } }); addStyle(2, 0, estiloSubtitulo);
    wsData.push([`Total pagos: ${historialPagos.length}   |   Total pagado: ${formatMoney(totalGeneral)}`]);
    merges.push({ s: { r: 3, c: 0 }, e: { r: 3, c: NCOL } }); addStyle(3, 0, estiloResumen);
    wsData.push([]);

    gruposArr.forEach((grupo, idx) => {
      const color = coloresProveedor[idx % coloresProveedor.length];
      const estiloProveedor = { font: { bold: true, sz: 12, color: { rgb: color.fg } }, fill: { fgColor: { rgb: color.bg } }, alignment: { horizontal: 'left', vertical: 'center' } };
      const nFacturas = new Set(grupo.pagos.map(p => p.numero_factura)).size;
      const nEventos = new Set(grupo.pagos.map(evKey)).size;

      const provRow = wsData.length;
      wsData.push([`  ${grupo.nombre}   |   NIT: ${grupo.nit || '-'}   |   ${nEventos} pago(s)   |   ${nFacturas} factura(s)   |   Total: ${formatMoney(grupo.total)}`]);
      merges.push({ s: { r: provRow, c: 0 }, e: { r: provRow, c: NCOL } }); addStyle(provRow, 0, estiloProveedor);

      // Dentro de cada proveedor se agrupa por COMPROBANTE, igual que en pantalla:
      // un solo PDF cubre varias facturas, así que el link va una vez en la
      // cabecera del grupo y debajo van las facturas que quedaron en ese pago.
      const eventos = new Map();
      grupo.pagos.forEach(p => {
        const k = evKey(p);
        if (!eventos.has(k)) {
          eventos.set(k, {
            fecha: p.fecha_pago, metodo: p.metodo_pago, referencia: p.referencia,
            banco: p.banco, link: linkPago(p), pagos: [], total: 0,
          });
        }
        const ev = eventos.get(k);
        ev.pagos.push(p);
        ev.total += parseFloat(p.monto_pago || 0);
      });
      const eventosArr = [...eventos.values()].sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''));

      eventosArr.forEach(ev => {
        const evRow = wsData.length;
        wsData.push([
          `   ▸ Pago del ${formatDate(ev.fecha)}   |   ${ev.pagos.length} factura(s)   |   ${(ev.metodo || '-')}${ev.referencia ? `   |   Ref: ${ev.referencia}` : ''}${ev.banco ? `   |   ${ev.banco}` : ''}   |   Total: ${formatMoney(ev.total)}`,
          '', '', '', '', '', '', '', '', '',
          ev.link ? 'Ver comprobante del pago' : '(sin comprobante)',
        ]);
        merges.push({ s: { r: evRow, c: 0 }, e: { r: evRow, c: NCOL - 1 } });
        addStyle(evRow, 0, estiloEvento);
        if (ev.link) {
          const celda = XLSX.utils.encode_cell({ r: evRow, c: NCOL });
          hipervinculos.push({ celda, url: ev.link });
          addStyle(evRow, NCOL, estiloLink);
        } else addStyle(evRow, NCOL, estiloEvento);

        const headerRow = wsData.length;
        const headers = ['F. Pago', '# Factura', 'Proyecto', 'Cliente', 'Método', 'Referencia', 'Banco', 'Monto Pago', 'Monto Factura', 'Saldo Factura', 'Adjunto factura'];
        wsData.push(headers);
        headers.forEach((_, ci) => addStyle(headerRow, ci, estiloHeaderCol));

        ev.pagos.forEach(p => {
          const dataRow = wsData.length;
          const lf = linkFactura(p);
          wsData.push([
            formatDate(p.fecha_pago), p.numero_factura || '-', p.proyecto_nombre || '-', p.nombre_cliente || '-',
            p.metodo_pago || '-', p.referencia || '-', p.banco || '-',
            parseFloat(p.monto_pago || 0), parseFloat(p.factura_monto || 0), parseFloat(p.factura_saldo || 0),
            lf ? 'Ver factura' : '-',
          ]);
          for (let ci = 0; ci < NCOL; ci++) addStyle(dataRow, ci, (ci >= 7 && ci <= 9) ? estiloMoney : estiloCelda);
          if (lf) {
            hipervinculos.push({ celda: XLSX.utils.encode_cell({ r: dataRow, c: NCOL }), url: lf });
            addStyle(dataRow, NCOL, estiloLink);
          } else addStyle(dataRow, NCOL, estiloCelda);
        });
      });

      const subRow = wsData.length;
      wsData.push(['', '', '', '', '', '', 'Subtotal proveedor:', grupo.total, '', '', '']);
      addStyle(subRow, 6, { ...estiloCelda, font: { bold: true, sz: 10 }, alignment: { horizontal: 'right', vertical: 'center' } });
      addStyle(subRow, 7, { ...estiloMoney, font: { bold: true, sz: 10 } });
      wsData.push([]);
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    Object.entries(cellStyles).forEach(([cell, style]) => { if (ws[cell]) ws[cell].s = style; });
    ws['!cols'] = [{ wch: 13 }, { wch: 15 }, { wch: 22 }, { wch: 22 }, { wch: 14 }, { wch: 18 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 26 }];
    ws['!merges'] = merges;
    ws['!rows'] = wsData.map((_, i) => ({ hpt: i === 0 ? 28 : 20 }));
    // Los links quedan como hipervínculo real, clicable desde Excel.
    hipervinculos.forEach(({ celda, url }) => { if (ws[celda]) ws[celda].l = { Target: url, Tooltip: 'Abrir documento' }; });
    XLSX.utils.book_append_sheet(wb, ws, 'Historial de Pagos');

    // ===== HOJA 2: Por fecha, para cruzar con el banco =====
    // Una fila por FACTURA pagada, ordenadas por fecha, con su propio adjunto (el
    // que se sube al montar la factura). El comprobante del pago va aparte, en su
    // columna: una misma salida de plata cubre varias facturas, y en el banco se
    // ve como un solo movimiento, asi que los subtotales por dia son los que
    // cuadran contra el extracto.
    const bData = []; const bStyles = {}; const bMerges = []; const bLinks = [];
    const bAdd = (r, c, st) => { bStyles[XLSX.utils.encode_cell({ r, c })] = st; };
    const BCOL = 9;

    bData.push(['TUVI - PAGOS POR FECHA (CONCILIACIÓN BANCARIA)']);
    bMerges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: BCOL } }); bAdd(0, 0, estiloTitulo);
    bData.push([`Rango: ${filtros.fecha_desde || '—'} a ${filtros.fecha_hasta || '—'}   |   Los links vencen en ${diasValidez} días`]);
    bMerges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: BCOL } }); bAdd(1, 0, estiloSubtitulo);
    bData.push([]);

    // Más viejo primero: es el orden en que se lee un extracto bancario.
    const pagosPorFecha = [...historialPagos].sort((a, b) =>
      (a.fecha_pago || '').localeCompare(b.fecha_pago || '') ||
      (a.proveedor_nombre || '').localeCompare(b.proveedor_nombre || ''));

    const bh = bData.length;
    const bHeaders = ['Fecha', 'Proveedor', 'NIT', '# Factura', 'Proyecto', 'Método', 'Referencia', 'Banco', 'Valor pagado', 'Cuenta de cobro / soporte'];
    bData.push(bHeaders);
    bHeaders.forEach((_, ci) => bAdd(bh, ci, estiloHeaderCol));

    let fechaAnterior = null;
    let subtotalDia = 0;
    const cerrarDia = () => {
      if (fechaAnterior === null) return;
      const rr = bData.length;
      bData.push(['', `Total ${formatDate(fechaAnterior)}`, '', '', '', '', '', '', subtotalDia, '']);
      bAdd(rr, 1, { ...estiloCelda, font: { bold: true, sz: 10 }, fill: { fgColor: { rgb: 'F1F8E9' } } });
      bAdd(rr, 8, { ...estiloMoney, font: { bold: true, sz: 10 }, fill: { fgColor: { rgb: 'F1F8E9' } } });
      subtotalDia = 0;
    };

    let filasBanco = 0;
    pagosPorFecha.forEach(p => {
      const dia = (p.fecha_pago || '').slice(0, 10);
      if (fechaAnterior !== null && dia !== fechaAnterior) cerrarDia();
      fechaAnterior = dia;
      const valor = parseFloat(p.monto_pago || 0);
      subtotalDia += valor;
      filasBanco += 1;

      // El soporte que envía el contratista para cobrar, que es el que se adjunta
      // al montar la factura. El comprobante del pago ya va en la primera hoja.
      const lf = linkFactura(p);
      const rr = bData.length;
      bData.push([
        formatDate(p.fecha_pago), p.proveedor_nombre || '-', p.proveedor_nit || '-',
        p.numero_factura || '-', p.proyecto_nombre || '-',
        p.metodo_pago || '-', p.referencia || '-', p.banco || '-', valor,
        lf ? 'Ver cuenta de cobro' : '-',
      ]);
      for (let ci = 0; ci <= BCOL; ci++) bAdd(rr, ci, ci === 8 ? estiloMoney : estiloCelda);
      if (lf) {
        bLinks.push({ celda: XLSX.utils.encode_cell({ r: rr, c: BCOL }), url: lf });
        bAdd(rr, BCOL, estiloLink);
      }
    });
    cerrarDia();

    const bt = bData.length;
    bData.push(['', 'TOTAL DEL RANGO', '', '', '', '', '', '', totalGeneral, '']);
    bAdd(bt, 1, { ...estiloCelda, font: { bold: true, sz: 11 } });
    bAdd(bt, 8, { ...estiloMoney, font: { bold: true, sz: 11, color: { rgb: '2E7D32' } } });

    const wsB = XLSX.utils.aoa_to_sheet(bData);
    Object.entries(bStyles).forEach(([cell, style]) => { if (wsB[cell]) wsB[cell].s = style; });
    bLinks.forEach(({ celda, url }) => { if (wsB[celda]) wsB[celda].l = { Target: url, Tooltip: 'Abrir documento' }; });
    wsB['!cols'] = [{ wch: 14 }, { wch: 32 }, { wch: 15 }, { wch: 14 }, { wch: 26 }, { wch: 14 }, { wch: 18 }, { wch: 15 }, { wch: 17 }, { wch: 24 }];
    wsB['!merges'] = bMerges;
    wsB['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: bh, c: 0 }, e: { r: bh + filasBanco, c: BCOL } }) };
    XLSX.utils.book_append_sheet(wb, wsB, 'Por Fecha (Banco)');

    // ===== HOJA 2: Resumen por proveedor =====
    const rData = []; const rStyles = {}; const rMerges = [];
    rData.push(['RESUMEN POR PROVEEDOR']); rMerges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } });
    rStyles[XLSX.utils.encode_cell({ r: 0, c: 0 })] = estiloTitulo;
    rData.push([]);
    const rh = rData.length;
    ['Proveedor', 'NIT', 'N° Pagos', 'N° Facturas', 'Total Pagado'].forEach((h, ci) => { rStyles[XLSX.utils.encode_cell({ r: rh, c: ci })] = estiloHeaderCol; });
    rData.push(['Proveedor', 'NIT', 'N° Pagos', 'N° Facturas', 'Total Pagado']);
    gruposArr.forEach(g => {
      const nFacturas = new Set(g.pagos.map(p => p.numero_factura)).size;
      const nEventos = new Set(g.pagos.map(evKey)).size;
      const rr = rData.length;
      rData.push([g.nombre, g.nit || '-', nEventos, nFacturas, g.total]);
      for (let ci = 0; ci < 5; ci++) rStyles[XLSX.utils.encode_cell({ r: rr, c: ci })] = (ci === 4 ? estiloMoney : estiloCelda);
    });
    const rt = rData.length;
    rData.push(['TOTAL', '', '', '', totalGeneral]);
    rStyles[XLSX.utils.encode_cell({ r: rt, c: 0 })] = { ...estiloCelda, font: { bold: true, sz: 11 } };
    rStyles[XLSX.utils.encode_cell({ r: rt, c: 4 })] = { ...estiloMoney, font: { bold: true, sz: 11, color: { rgb: '2E7D32' } } };
    const wsR = XLSX.utils.aoa_to_sheet(rData);
    Object.entries(rStyles).forEach(([cell, style]) => { if (wsR[cell]) wsR[cell].s = style; });
    wsR['!cols'] = [{ wch: 34 }, { wch: 16 }, { wch: 12 }, { wch: 12 }, { wch: 20 }];
    wsR['!merges'] = rMerges;
    XLSX.utils.book_append_sheet(wb, wsR, 'Resumen');

    XLSX.writeFile(wb, `TUVI_Historial_Pagos_${new Date().toISOString().split('T')[0]}.xlsx`);
    success('Historial exportado a Excel');
  };

  // Exportar a Excel con formato profesional y colores
  const exportarExcel = async () => {
    if (facturasPendientes.length === 0) {
      error('No hay facturas para exportar');
      return;
    }

    // Link del adjunto de cada factura (el mismo que abre el clip en pantalla).
    // Va con un token propio del archivo, con vencimiento, igual que en el
    // historial: el Excel se puede compartir sin repartir la sesión.
    const mapaLinks = new Map();
    try {
      const d = await client.query(
        `query($ids: [Int!]!) { linksArchivosFacturas(factura_ids: $ids) { links { clave url } } }`,
        { ids: facturasPendientes.map(f => f.id) });
      (d.linksArchivosFacturas?.links || []).forEach(l => { if (l.url) mapaLinks.set(l.clave, l.url); });
    } catch (e) {
      console.error('No se pudieron generar los links de los adjuntos:', e);
      error('El Excel saldrá sin los links de los adjuntos');
    }
    const linkFactura = (f) => mapaLinks.get((f.archivo_factura || '').trim()) || '';
    const hipervinculos = [];

    const wb = XLSX.utils.book_new();
    const fecha = new Date().toISOString().split('T')[0];
    const totalPendiente = facturasAgrupadas.reduce((s, g) => s + g.total_saldo, 0);

    // Paleta de colores para proveedores
    const coloresProveedor = [
      { bg: '1A2432', fg: 'FFFFFF' },
      { bg: '2E7D32', fg: 'FFFFFF' },
      { bg: '1565C0', fg: 'FFFFFF' },
      { bg: '6A1B9A', fg: 'FFFFFF' },
      { bg: 'C62828', fg: 'FFFFFF' },
      { bg: 'E65100', fg: 'FFFFFF' },
      { bg: '00695C', fg: 'FFFFFF' },
      { bg: '4527A0', fg: 'FFFFFF' },
      { bg: '283593', fg: 'FFFFFF' },
      { bg: '33691E', fg: 'FFFFFF' },
      { bg: 'BF360C', fg: 'FFFFFF' },
      { bg: '01579B', fg: 'FFFFFF' },
    ];

    // Estilos comunes
    const estiloTitulo = { font: { bold: true, sz: 16, color: { rgb: '1A2432' } }, alignment: { horizontal: 'left', vertical: 'center' } };
    const estiloSubtitulo = { font: { sz: 11, color: { rgb: '555555' } }, alignment: { horizontal: 'left' } };
    const estiloResumen = { font: { bold: true, sz: 12, color: { rgb: '2E7D32' } }, alignment: { horizontal: 'left' } };
    const estiloHeaderCol = {
      font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } },
      fill: { fgColor: { rgb: '37474F' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: { bottom: { style: 'thin', color: { rgb: '000000' } } }
    };
    const estiloCelda = {
      font: { sz: 10 },
      alignment: { vertical: 'center' },
      border: { bottom: { style: 'hair', color: { rgb: 'DDDDDD' } } }
    };
    const estiloMoney = {
      ...estiloCelda,
      numFmt: '#,##0',
      alignment: { horizontal: 'right', vertical: 'center' }
    };
    const estiloVencida = { font: { bold: true, sz: 10, color: { rgb: 'C62828' } } };
    const estiloParcial = { font: { bold: true, sz: 10, color: { rgb: 'E65100' } } };
    const estiloLink = { ...estiloCelda, font: { sz: 10, color: { rgb: '1565C0' }, underline: true }, alignment: { horizontal: 'center', vertical: 'center' } };

    // ===== HOJA 1: Detalle por Proveedor =====
    const wsData = [];
    const merges = [];
    const cellStyles = {};

    const addStyle = (row, col, style) => {
      const cell = XLSX.utils.encode_cell({ r: row, c: col });
      cellStyles[cell] = style;
    };

    // Título
    let r = 0;
    wsData.push(['TUVI - REPORTE DE FACTURAS PENDIENTES DE PAGO']);
    merges.push({ s: { r, c: 0 }, e: { r, c: 12 } });
    addStyle(r, 0, estiloTitulo);

    r = 1;
    wsData.push([`Fecha de generación: ${new Date().toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`]);
    merges.push({ s: { r, c: 0 }, e: { r, c: 12 } });
    addStyle(r, 0, estiloSubtitulo);

    r = 2;
    wsData.push([`Total facturas: ${facturasPendientes.length}  |  Total pendiente: ${formatMoney(totalPendiente)}`]);
    merges.push({ s: { r, c: 0 }, e: { r, c: 12 } });
    addStyle(r, 0, estiloResumen);

    wsData.push([]);

    facturasAgrupadas.forEach((grupo, idx) => {
      const color = coloresProveedor[idx % coloresProveedor.length];
      const estiloProveedor = {
        font: { bold: true, sz: 12, color: { rgb: color.fg } },
        fill: { fgColor: { rgb: color.bg } },
        alignment: { horizontal: 'left', vertical: 'center' }
      };

      const provRow = wsData.length;
      wsData.push([`  ${grupo.proveedor_nombre}  |  NIT: ${grupo.proveedor_nit || '-'}  |  ${grupo.facturas.length} factura(s)  |  Total: ${formatMoney(grupo.total_saldo)}`]);
      merges.push({ s: { r: provRow, c: 0 }, e: { r: provRow, c: 12 } });
      addStyle(provRow, 0, estiloProveedor);

      // Headers de columna
      const headerRow = wsData.length;
      const headers = ['# Factura', 'Proyecto', 'Apto', 'Descripción', 'F. Emisión', 'F. Vencimiento', 'Monto', 'Pagado', 'Retenciones', 'Saldo Pendiente', 'Estado', 'Días', 'Adjunto factura'];
      wsData.push(headers);
      headers.forEach((_, ci) => addStyle(headerRow, ci, estiloHeaderCol));

      grupo.facturas.forEach(f => {
        const dataRow = wsData.length;
        const lf = linkFactura(f);
        const estado = f.urgencia === 'vencida' ? 'VENCIDA' : f.estado === 'pagado_parcial' ? 'PARCIAL' : 'PENDIENTE';
        wsData.push([
          f.numero_factura,
          f.proyecto_nombre || '-',
          f.apartamento || '-',
          f.descripcion || '-',
          f.fecha_emision || '-',
          f.fecha_vencimiento || '-',
          parseFloat(f.monto || 0),
          parseFloat(f.monto_pagado || 0),
          parseFloat(f.total_retenciones || 0),
          parseFloat(f.saldo_real || f.saldo_pendiente || 0),
          estado,
          f.dias_vencido || 0,
          lf ? 'Ver adjunto' : '-'
        ]);

        // Estilo para cada celda de datos
        for (let ci = 0; ci < 12; ci++) {
          if (ci >= 6 && ci <= 9) {
            addStyle(dataRow, ci, estiloMoney);
          } else {
            addStyle(dataRow, ci, estiloCelda);
          }
        }
        // Columna del adjunto: hipervínculo real, clicable desde Excel
        if (lf) {
          hipervinculos.push({ celda: XLSX.utils.encode_cell({ r: dataRow, c: 12 }), url: lf });
          addStyle(dataRow, 12, estiloLink);
        } else {
          addStyle(dataRow, 12, { ...estiloCelda, alignment: { horizontal: 'center', vertical: 'center' } });
        }
        // Color para estado
        if (estado === 'VENCIDA') addStyle(dataRow, 10, { ...estiloCelda, ...estiloVencida });
        else if (estado === 'PARCIAL') addStyle(dataRow, 10, { ...estiloCelda, ...estiloParcial });
      });

      wsData.push([]);
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Aplicar estilos a celdas
    Object.entries(cellStyles).forEach(([cell, style]) => {
      if (ws[cell]) {
        ws[cell].s = style;
      }
    });

    ws['!cols'] = [
      { wch: 16 }, { wch: 24 }, { wch: 10 }, { wch: 38 }, { wch: 16 },
      { wch: 16 }, { wch: 20 }, { wch: 18 }, { wch: 16 }, { wch: 22 },
      { wch: 14 }, { wch: 10 }, { wch: 16 }
    ];
    ws['!merges'] = merges;
    ws['!rows'] = wsData.map((_, i) => ({ hpt: i === 0 ? 28 : 20 }));
    // Los links del adjunto quedan como hipervínculo real, clicable desde Excel.
    hipervinculos.forEach(({ celda, url }) => { if (ws[celda]) ws[celda].l = { Target: url, Tooltip: 'Abrir adjunto de la factura' }; });

    XLSX.utils.book_append_sheet(wb, ws, 'Detalle por Proveedor');

    // ===== HOJA 2: Resumen por Proveedor =====
    const resumenData = [];
    const resumenStyles = {};
    const resumenMerges = [];

    resumenData.push(['RESUMEN POR PROVEEDOR']);
    resumenMerges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } });
    resumenStyles[XLSX.utils.encode_cell({ r: 0, c: 0 })] = estiloTitulo;

    resumenData.push([]);

    const rh = 2;
    resumenData.push(['Proveedor', 'NIT', 'Cant. Facturas', 'Total Facturado', 'Total Pendiente', '% del Total']);
    for (let ci = 0; ci < 6; ci++) {
      resumenStyles[XLSX.utils.encode_cell({ r: rh, c: ci })] = estiloHeaderCol;
    }

    facturasAgrupadas.forEach((g, idx) => {
      const ri = resumenData.length;
      const color = coloresProveedor[idx % coloresProveedor.length];
      resumenData.push([
        g.proveedor_nombre,
        g.proveedor_nit || '-',
        g.facturas.length,
        g.total_monto,
        g.total_saldo,
        totalPendiente > 0 ? ((g.total_saldo / totalPendiente) * 100).toFixed(1) + '%' : '0%'
      ]);
      // Color lateral para proveedor
      resumenStyles[XLSX.utils.encode_cell({ r: ri, c: 0 })] = {
        font: { bold: true, sz: 10, color: { rgb: color.bg } },
        border: { left: { style: 'medium', color: { rgb: color.bg } } }
      };
      resumenStyles[XLSX.utils.encode_cell({ r: ri, c: 3 })] = estiloMoney;
      resumenStyles[XLSX.utils.encode_cell({ r: ri, c: 4 })] = estiloMoney;
    });

    resumenData.push([]);
    const totalRow = resumenData.length;
    resumenData.push(['TOTAL', '', facturasPendientes.length, facturasAgrupadas.reduce((s, g) => s + g.total_monto, 0), totalPendiente, '100%']);
    for (let ci = 0; ci < 6; ci++) {
      resumenStyles[XLSX.utils.encode_cell({ r: totalRow, c: ci })] = {
        font: { bold: true, sz: 11 },
        fill: { fgColor: { rgb: 'E8F5E9' } },
        border: { top: { style: 'medium', color: { rgb: '2E7D32' } } }
      };
    }
    resumenStyles[XLSX.utils.encode_cell({ r: totalRow, c: 3 })] = { ...resumenStyles[XLSX.utils.encode_cell({ r: totalRow, c: 3 })], numFmt: '#,##0' };
    resumenStyles[XLSX.utils.encode_cell({ r: totalRow, c: 4 })] = { ...resumenStyles[XLSX.utils.encode_cell({ r: totalRow, c: 4 })], numFmt: '#,##0' };

    const wsResumen = XLSX.utils.aoa_to_sheet(resumenData);
    Object.entries(resumenStyles).forEach(([cell, style]) => {
      if (wsResumen[cell]) wsResumen[cell].s = style;
    });
    wsResumen['!cols'] = [{ wch: 40 }, { wch: 18 }, { wch: 18 }, { wch: 22 }, { wch: 22 }, { wch: 14 }];
    wsResumen['!merges'] = resumenMerges;

    XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen por Proveedor');

    // ===== HOJA 3: Lista plana completa (con formato igual a las demás) =====
    const listaData = [];
    const listaStyles = {};
    const listaMerges = [];

    const addListaStyle = (row, col, style) => {
      listaStyles[XLSX.utils.encode_cell({ r: row, c: col })] = style;
    };

    // Título
    listaData.push(['TUVI - LISTA COMPLETA DE FACTURAS PENDIENTES']);
    listaMerges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 13 } });
    addListaStyle(0, 0, estiloTitulo);

    listaData.push([`Fecha: ${new Date().toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}  |  ${facturasPendientes.length} facturas  |  Total: ${formatMoney(totalPendiente)}`]);
    listaMerges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: 13 } });
    addListaStyle(1, 0, estiloResumen);

    listaData.push([]);

    // Headers
    const listaHeaders = ['# Factura', 'Proveedor', 'NIT', 'Proyecto', 'Apto', 'Descripción', 'F. Emisión', 'F. Vencimiento', 'Monto Total', 'Monto Pagado', 'Retenciones', 'Saldo Pendiente', 'Estado', 'Días Vencido'];
    const hRow = listaData.length;
    listaData.push(listaHeaders);
    listaHeaders.forEach((_, ci) => addListaStyle(hRow, ci, estiloHeaderCol));

    // Data rows
    facturasPendientes.forEach(f => {
      const dataRow = listaData.length;
      const estado = f.urgencia === 'vencida' ? 'VENCIDA' : f.estado === 'pagado_parcial' ? 'PAGO PARCIAL' : 'PENDIENTE';
      listaData.push([
        f.numero_factura,
        f.proveedor_nombre,
        f.proveedor_nit || '-',
        f.proyecto_nombre || '-',
        f.apartamento || '-',
        f.descripcion || '-',
        f.fecha_emision || '-',
        f.fecha_vencimiento || '-',
        parseFloat(f.monto || 0),
        parseFloat(f.monto_pagado || 0),
        parseFloat(f.total_retenciones || 0),
        parseFloat(f.saldo_real || f.saldo_pendiente || 0),
        estado,
        f.dias_vencido || 0
      ]);

      // Estilos por celda
      for (let ci = 0; ci < 14; ci++) {
        if (ci >= 8 && ci <= 11) {
          addListaStyle(dataRow, ci, estiloMoney);
        } else {
          addListaStyle(dataRow, ci, estiloCelda);
        }
      }
      if (estado === 'VENCIDA') addListaStyle(dataRow, 12, { ...estiloCelda, ...estiloVencida });
      else if (estado === 'PAGO PARCIAL') addListaStyle(dataRow, 12, { ...estiloCelda, ...estiloParcial });
    });

    // Fila de totales
    listaData.push([]);
    const totRow = listaData.length;
    const estiloTotal = {
      font: { bold: true, sz: 11 },
      fill: { fgColor: { rgb: 'E8F5E9' } },
      border: { top: { style: 'medium', color: { rgb: '2E7D32' } } }
    };
    listaData.push([
      'TOTALES', '', '', '', '', '', '', '',
      facturasPendientes.reduce((s, f) => s + parseFloat(f.monto || 0), 0),
      facturasPendientes.reduce((s, f) => s + parseFloat(f.monto_pagado || 0), 0),
      facturasPendientes.reduce((s, f) => s + parseFloat(f.total_retenciones || 0), 0),
      totalPendiente,
      '', ''
    ]);
    for (let ci = 0; ci < 14; ci++) {
      if (ci >= 8 && ci <= 11) {
        addListaStyle(totRow, ci, { ...estiloTotal, numFmt: '#,##0' });
      } else {
        addListaStyle(totRow, ci, estiloTotal);
      }
    }

    const wsPlana = XLSX.utils.aoa_to_sheet(listaData);
    Object.entries(listaStyles).forEach(([cell, style]) => {
      if (wsPlana[cell]) wsPlana[cell].s = style;
    });
    wsPlana['!cols'] = [
      { wch: 16 }, { wch: 38 }, { wch: 18 }, { wch: 24 }, { wch: 10 },
      { wch: 38 }, { wch: 16 }, { wch: 16 }, { wch: 20 }, { wch: 18 },
      { wch: 16 }, { wch: 22 }, { wch: 14 }, { wch: 12 }
    ];
    wsPlana['!merges'] = listaMerges;
    wsPlana['!rows'] = listaData.map((_, i) => ({ hpt: i === 0 ? 28 : 20 }));
    XLSX.utils.book_append_sheet(wb, wsPlana, 'Lista Completa');

    XLSX.writeFile(wb, `Facturas_Pendientes_TUVI_${fecha}.xlsx`);
    success('Excel exportado con 3 hojas: Detalle, Resumen y Lista completa');
  };

  // Abrir adjunto de factura
  const abrirAdjuntoFactura = (ruta) => {
    window.open(`${UPLOAD_URL}/factura-file/${encodeURIComponent(ruta)}`, '_blank');
  };

  if (loading && !estadisticas) {
    return (
      <div className="pagos-loading">
        <div className="loading-spinner">⏳</div>
        <p>Cargando módulo de pagos...</p>
      </div>
    );
  }

  return (
    <div className="pagos-container">
      {/* Header */}
      <div className="pagos-header">
        <div className="header-info">
          <h1>💳 Gestión de Pagos</h1>
          <p>Control y registro de pagos a proveedores</p>
        </div>
        <div className="header-actions">
          <div className="vista-tabs">
            <button
              className={`tab-btn ${vista === 'dashboard' ? 'active' : ''}`}
              onClick={() => setVista('dashboard')}
            >
              📊 Dashboard
            </button>
            <button
              className={`tab-btn ${vista === 'pendientes' ? 'active' : ''}`}
              onClick={() => setVista('pendientes')}
            >
              📋 Pendientes
            </button>
            <button
              className={`tab-btn ${vista === 'historial' ? 'active' : ''}`}
              onClick={() => setVista('historial')}
            >
              📜 Historial
            </button>
            <button
              className={`tab-btn ${vista === 'retefuente' ? 'active' : ''}`}
              onClick={() => setVista('retefuente')}
            >
              🧾 Retefuente
            </button>
            <button
              className={`tab-btn ${vista === 'ajustes' ? 'active' : ''}`}
              onClick={() => setVista('ajustes')}
            >
              ↩️ Reintegros / Descuentos
            </button>
          </div>
        </div>
      </div>

      {/* Vista Reintegros / Descuentos por incumplimiento */}
      {vista === 'ajustes' && <AjustesCartera />}

      {/* Vista Dashboard */}
      {vista === 'dashboard' && estadisticas && (
        <div className="dashboard-view">
          {/* Tarjetas de Resumen */}
          <div className="resumen-cards">
            <div className="resumen-card pendiente">
              <div className="card-icon">📋</div>
              <div className="card-content">
                <span className="card-value">{estadisticas.resumen.facturas_pendientes}</span>
                <span className="card-label">Facturas Pendientes</span>
                <span className="card-monto">{formatMoney(estadisticas.resumen.monto_pendiente)}</span>
              </div>
            </div>

            <div className="resumen-card vencido">
              <div className="card-icon">⚠️</div>
              <div className="card-content">
                <span className="card-value">{estadisticas.resumen.facturas_vencidas}</span>
                <span className="card-label">Facturas Vencidas</span>
                <span className="card-monto">{formatMoney(estadisticas.resumen.monto_vencido)}</span>
              </div>
            </div>

            <div className="resumen-card pagado">
              <div className="card-icon">✅</div>
              <div className="card-content">
                <span className="card-value">{estadisticas.resumen.pagos_mes}</span>
                <span className="card-label">Pagos este Mes</span>
                <span className="card-monto">{formatMoney(estadisticas.resumen.pagado_mes)}</span>
              </div>
            </div>
          </div>

          {/* Proyección de Pagos */}
          <div className="proyeccion-section">
            <h2>📅 Proyección de Pagos</h2>
            <div className="proyeccion-cards">
              <div className="proyeccion-card dias-7">
                <span className="proyeccion-periodo">Próximos 7 días</span>
                <span className="proyeccion-facturas">{estadisticas.proyeccion.dias_7.facturas} facturas</span>
                <span className="proyeccion-monto">{formatMoney(estadisticas.proyeccion.dias_7.monto)}</span>
              </div>
              <div className="proyeccion-card dias-15">
                <span className="proyeccion-periodo">Próximos 15 días</span>
                <span className="proyeccion-facturas">{estadisticas.proyeccion.dias_15.facturas} facturas</span>
                <span className="proyeccion-monto">{formatMoney(estadisticas.proyeccion.dias_15.monto)}</span>
              </div>
              <div className="proyeccion-card dias-30">
                <span className="proyeccion-periodo">Próximos 30 días</span>
                <span className="proyeccion-facturas">{estadisticas.proyeccion.dias_30.facturas} facturas</span>
                <span className="proyeccion-monto">{formatMoney(estadisticas.proyeccion.dias_30.monto)}</span>
              </div>
            </div>
          </div>

          {/* Alertas */}
          <div className="alertas-section">
            {/* Facturas Vencidas */}
            {estadisticas.alertas.vencidas.length > 0 && (
              <div className="alerta-grupo vencidas">
                <h3>🔴 Facturas Vencidas ({estadisticas.alertas.vencidas.length})</h3>
                <div className="alertas-lista">
                  {estadisticas.alertas.vencidas.slice(0, 5).map(f => (
                    <div key={f.id} className="alerta-item vencida">
                      <div className="alerta-info">
                        <span className="alerta-factura">#{f.numero_factura}</span>
                        <span className="alerta-proveedor">{f.proveedor_nombre}</span>
                        <span className="alerta-dias">{f.dias_vencido} días de atraso</span>
                      </div>
                      <div className="alerta-monto">
                        {formatMoney(f.saldo_pendiente)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Por vencer en 7 días */}
            {estadisticas.alertas.por_vencer_7_dias.length > 0 && (
              <div className="alerta-grupo proximas">
                <h3>🟡 Por Vencer en 7 días ({estadisticas.alertas.por_vencer_7_dias.length})</h3>
                <div className="alertas-lista">
                  {estadisticas.alertas.por_vencer_7_dias.slice(0, 5).map(f => (
                    <div key={f.id} className="alerta-item proxima">
                      <div className="alerta-info">
                        <span className="alerta-factura">#{f.numero_factura}</span>
                        <span className="alerta-proveedor">{f.proveedor_nombre}</span>
                        <span className="alerta-dias">Vence en {f.dias_para_vencer} días</span>
                      </div>
                      <div className="alerta-monto">
                        {formatMoney(f.saldo_pendiente)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Top Proveedores con Saldo */}
          {estadisticas.por_proveedor.length > 0 && (
            <div className="proveedores-section">
              <h2>🏢 Proveedores con Mayor Saldo Pendiente</h2>
              <div className="proveedores-tabla">
                <table>
                  <thead>
                    <tr>
                      <th>Proveedor</th>
                      <th>NIT</th>
                      <th>Facturas</th>
                      <th>Vencidas</th>
                      <th>Saldo Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {estadisticas.por_proveedor.map(p => (
                      <tr key={p.proveedor_id}>
                        <td className="proveedor-nombre">{p.proveedor_nombre}</td>
                        <td>{p.nit || '-'}</td>
                        <td className="center">{p.total_facturas}</td>
                        <td className={`center ${p.facturas_vencidas > 0 ? 'text-danger' : ''}`}>
                          {p.facturas_vencidas}
                        </td>
                        <td className="monto">{formatMoney(p.saldo_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Botón para ir a pendientes */}
          <div className="dashboard-action">
            <button className="btn-ir-pendientes" onClick={() => setVista('pendientes')}>
              💳 Ir a Registrar Pagos
            </button>
          </div>
        </div>
      )}

      {/* Vista Facturas Pendientes - Agrupadas por Proveedor */}
      {vista === 'pendientes' && (
        <div className="pendientes-view">
          {/* Filtros */}
          <div className="filtros-section">
            <div className="filtros-row">
              <div className="filtro-group">
                <label>Estado</label>
                <select
                  value={filtros.estado}
                  onChange={(e) => handleFiltroChange('estado', e.target.value)}
                >
                  <option value="">Todas</option>
                  <option value="vencida">Vencidas</option>
                  <option value="proxima">Por vencer (7 días)</option>
                  <option value="pendiente">Pendientes</option>
                  <option value="parcial">Pago Parcial</option>
                </select>
              </div>

              <div className="filtro-group">
                <label>Ordenar por</label>
                <select
                  value={filtros.ordenar}
                  onChange={(e) => handleFiltroChange('ordenar', e.target.value)}
                >
                  <option value="vencimiento">Fecha Vencimiento</option>
                  <option value="monto">Monto (Mayor a Menor)</option>
                  <option value="antiguedad">Antigüedad</option>
                  <option value="proveedor">Proveedor</option>
                </select>
              </div>

              <div className="filtro-group busqueda">
                <label>Buscar</label>
                <input
                  type="text"
                  placeholder="# factura, proveedor, proyecto..."
                  value={filtros.busqueda}
                  onChange={(e) => handleFiltroChange('busqueda', e.target.value)}
                />
              </div>

              <div className="filtro-group">
                <label>Vencimiento desde</label>
                <input type="date" value={filtros.venc_desde || ''} onChange={(e) => handleFiltroChange('venc_desde', e.target.value)} />
              </div>
              <div className="filtro-group">
                <label>Vencimiento hasta</label>
                <input type="date" value={filtros.venc_hasta || ''} onChange={(e) => handleFiltroChange('venc_hasta', e.target.value)} />
              </div>
              {(filtros.venc_desde || filtros.venc_hasta) && (
                <div className="filtro-group" style={{ alignSelf: 'flex-end' }}>
                  <button className="btn-limpiar-filtro" onClick={() => { handleFiltroChange('venc_desde', ''); handleFiltroChange('venc_hasta', ''); }} style={{ padding: '6px 12px', background: '#f1f5f9', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: '0.82rem' }}>Limpiar fechas</button>
                </div>
              )}
            </div>
          </div>

          {/* Botón Exportar + Resumen */}
          <div className="pendientes-toolbar">
            <div className="pendientes-resumen">
              <span className="resumen-item">
                <strong>{facturasPendientes.length}</strong> facturas de <strong>{facturasAgrupadas.length}</strong> proveedores
              </span>
              <span className="resumen-item total">
                Total pendiente: <strong>{formatMoney(facturasAgrupadas.reduce((s, g) => s + g.total_saldo, 0))}</strong>
              </span>
            </div>
            <button className="btn-exportar-excel" onClick={exportarExcel} disabled={facturasPendientes.length === 0}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Exportar Excel
            </button>
          </div>

          {/* Acciones de selección */}
          {facturasSeleccionadas.length > 0 && (
            <div className="seleccion-actions">
              <span className="seleccion-info">
                {facturasSeleccionadas.length} factura(s) seleccionada(s) -
                Total: {formatMoney(facturasSeleccionadas.reduce((sum, f) => sum + (f.saldo_real || f.saldo_pendiente || 0), 0))}
              </span>
              <button
                className="btn-pagar-seleccion"
                onClick={() => setMostrarModalPago(true)}
              >
                Pagar Seleccionadas
              </button>
              <button
                className="btn-limpiar-seleccion"
                onClick={() => setFacturasSeleccionadas([])}
              >
                Limpiar
              </button>
            </div>
          )}

          {/* Lista Agrupada por Proveedor */}
          <div className="facturas-agrupadas">
            {loading ? (
              <div className="loading-state">Cargando facturas...</div>
            ) : facturasAgrupadas.length === 0 ? (
              <div className="empty-state">
                <span>🎉</span>
                <p>No hay facturas pendientes de pago</p>
              </div>
            ) : (
              facturasAgrupadas.map(grupo => (
                <div key={grupo.proveedor_id} className={`proveedor-grupo ${gruposExpandidos[grupo.proveedor_id] ? 'expandido' : 'contraido'}`}>
                  <div className="proveedor-grupo-header" onClick={() => toggleGrupo(grupo.proveedor_id)}>
                    <div className="proveedor-grupo-info">
                      <span className="grupo-chevron">{gruposExpandidos[grupo.proveedor_id] ? '▼' : '▶'}</span>
                      <div>
                        <h3>{grupo.proveedor_nombre}</h3>
                        {grupo.proveedor_nit && <span className="grupo-nit">NIT: {grupo.proveedor_nit}</span>}
                      </div>
                    </div>
                    <div className="proveedor-grupo-stats">
                      <span className="grupo-cant">{grupo.facturas.length} factura{grupo.facturas.length > 1 ? 's' : ''}</span>
                      <span className="grupo-total">{formatMoney(grupo.total_saldo)}</span>
                    </div>
                  </div>
                  {gruposExpandidos[grupo.proveedor_id] && <div className="proveedor-grupo-body">
                    <div className="grupo-facturas-header">
                      <div className="gcol-check">
                        <input
                          type="checkbox"
                          checked={grupo.facturas.every(f => facturasSeleccionadas.find(s => s.id === f.id))}
                          onChange={() => {
                            const todasSel = grupo.facturas.every(f => facturasSeleccionadas.find(s => s.id === f.id));
                            if (todasSel) {
                              setFacturasSeleccionadas(prev => prev.filter(s => s.proveedor_id !== grupo.proveedor_id));
                            } else {
                              // Solo del mismo proveedor
                              const otrasProveedores = facturasSeleccionadas.filter(s => s.proveedor_id !== grupo.proveedor_id);
                              if (otrasProveedores.length > 0 && facturasSeleccionadas.length > 0) {
                                error('Solo puedes seleccionar facturas de un proveedor a la vez');
                                return;
                              }
                              setFacturasSeleccionadas(grupo.facturas);
                            }
                          }}
                        />
                      </div>
                      <div className="gcol-factura">Factura</div>
                      <div className="gcol-proyecto">Proyecto</div>
                      <div className="gcol-apto">Apto</div>
                      <div className="gcol-vencimiento">Vencimiento</div>
                      <div className="gcol-monto">Monto</div>
                      <div className="gcol-saldo">Saldo</div>
                      <div className="gcol-acciones">Acciones</div>
                    </div>
                    {grupo.facturas.map(factura => (
                      <div
                        key={factura.id}
                        className={`grupo-factura-row ${getUrgenciaClass(factura)} ${
                          facturasSeleccionadas.find(f => f.id === factura.id) ? 'selected' : ''
                        }`}
                      >
                        <div className="gcol-check">
                          <input
                            type="checkbox"
                            checked={!!facturasSeleccionadas.find(f => f.id === factura.id)}
                            onChange={() => toggleSeleccionFactura(factura)}
                          />
                        </div>
                        <div className="gcol-factura">
                          <span className="factura-numero">#{factura.numero_factura}</span>
                          {factura.estado === 'pagado_parcial' && (
                            <span className="badge-parcial">Parcial</span>
                          )}
                          {factura.descripcion && (
                            <span className="factura-desc">{factura.descripcion}</span>
                          )}
                        </div>
                        <div className="gcol-proyecto">
                          {factura.proyecto_nombre || '-'}
                        </div>
                        <div className="gcol-apto">
                          {factura.apartamento || '-'}
                        </div>
                        <div className="gcol-vencimiento">
                          <span className="fecha">{formatDate(factura.fecha_vencimiento)}</span>
                          {getUrgenciaText(factura) && (
                            <span className={`urgencia-text ${factura.urgencia}`}>
                              {getUrgenciaText(factura)}
                            </span>
                          )}
                        </div>
                        <div className="gcol-monto">
                          {formatMoney(factura.monto)}
                        </div>
                        <div className="gcol-saldo">
                          <span className="saldo-valor">{formatMoney(factura.saldo_real)}</span>
                          {factura.total_retenciones > 0 && (
                            <span className="badge-retencion" title={`Retención: ${formatMoney(factura.total_retenciones)} | Saldo bruto: ${formatMoney(factura.saldo_pendiente)}`}>
                              Ret: {formatMoney(factura.total_retenciones)}
                            </span>
                          )}
                          {factura.monto_pagado > 0 && factura.estado === 'pagado_parcial' && (
                            <span className="badge-parcial" title={`Pagado: ${formatMoney(factura.monto_pagado)} de ${formatMoney(factura.monto)}`}>
                              Abonado: {formatMoney(factura.monto_pagado)}
                            </span>
                          )}
                        </div>
                        <div className="gcol-acciones">
                          {factura.archivo_factura && (
                            <button
                              className="btn-ver-adjunto-pago"
                              onClick={() => abrirAdjuntoFactura(factura.archivo_factura)}
                              title="Ver factura adjunta"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                              </svg>
                            </button>
                          )}
                          <button
                            className="btn-pagar"
                            onClick={() => {
                              setFacturasSeleccionadas([factura]);
                              setMostrarModalPago(true);
                            }}
                            title="Registrar pago"
                          >
                            Pagar
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Vista Historial */}
      {vista === 'historial' && (
        <div className="historial-view">
          {/* Filtros del historial */}
          <div className="filtros-section">
            <div className="filtros-row filtros-historial">
              <div className="filtro-group busqueda">
                <label>Buscar</label>
                <input
                  type="text"
                  placeholder="# factura, proveedor, referencia..."
                  value={filtros.busqueda}
                  onChange={(e) => handleFiltroChange('busqueda', e.target.value)}
                />
              </div>

              <div className="filtro-group">
                <label>Desde</label>
                <input type="date" value={filtros.fecha_desde || ''} onChange={e => handleFiltroChange('fecha_desde', e.target.value)} />
              </div>
              <div className="filtro-group">
                <label>Hasta</label>
                <input type="date" value={filtros.fecha_hasta || ''} onChange={e => handleFiltroChange('fecha_hasta', e.target.value)} />
              </div>
            </div>
          </div>

          {/* Barra de exportar (exporta lo que está en el filtro actual) */}
          <div className="hist-export-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '4px 0 12px', flexWrap: 'wrap', gap: 10 }}>
            <span style={{ color: '#374151', fontSize: '0.9rem' }}>
              {historialPagos.length} pago(s) · Total: <strong style={{ color: '#2E7D32' }}>{formatMoney(historialPagos.reduce((s, p) => s + (p.monto_pago || 0), 0))}</strong>
            </span>
            <button className="btn-exportar-excel" onClick={exportarHistorialExcel} disabled={historialPagos.length === 0}>
              📊 Exportar Excel
            </button>
          </div>

          {/* Lista de Pagos agrupados por proveedor */}
          <div className="historial-lista">
            {loading ? (
              <div className="loading-state">Cargando historial...</div>
            ) : historialPagos.length === 0 ? (
              <div className="empty-state">
                <span>📋</span>
                <p>No hay pagos registrados</p>
              </div>
            ) : (() => {
              // Agrupar por proveedor
              const grupos = {};
              historialPagos.forEach(p => {
                const key = p.proveedor_id || 'sin_proveedor';
                if (!grupos[key]) grupos[key] = { nombre: p.proveedor_nombre || 'Sin proveedor', nit: p.proveedor_nit, pagos: [], total: 0 };
                grupos[key].pagos.push(p);
                grupos[key].total += p.monto_pago;
              });
              const gruposArr = Object.values(grupos).sort((a, b) => b.total - a.total);

              return (
                <div className="historial-grupos">
                  {gruposArr.map((grupo, gi) => {
                    const proveedorId = grupo.pagos[0]?.proveedor_id;
                    const grupoKey = proveedorId || `sin_${gi}`;
                    const expandido = !!gruposHistorialExpandidos[grupoKey];

                    // Agrupar pagos en "eventos" (cada evento = un registro de pago con su PDF)
                    const eventosMap = {};
                    grupo.pagos.forEach(p => {
                      const key = p.comprobante_ruta || `${p.fecha_pago || ''}__${p.referencia || ''}__${p.metodo_pago || ''}`;
                      if (!eventosMap[key]) {
                        eventosMap[key] = {
                          key,
                          fecha_pago: p.fecha_pago,
                          comprobante_ruta: p.comprobante_ruta,
                          comprobante_archivo: p.comprobante_archivo,
                          metodo_pago: p.metodo_pago,
                          referencia: p.referencia,
                          banco: p.banco,
                          pagos: [],
                          total: 0,
                        };
                      }
                      eventosMap[key].pagos.push(p);
                      eventosMap[key].total += p.monto_pago;
                    });
                    const eventos = Object.values(eventosMap).sort((a, b) => (b.fecha_pago || '').localeCompare(a.fecha_pago || ''));

                    return (
                    <div key={gi} className={`historial-grupo ${expandido ? 'expandido' : 'contraido'}`}>
                      <div className="historial-grupo-header clickable" onClick={() => setGruposHistorialExpandidos(prev => ({ ...prev, [grupoKey]: !prev[grupoKey] }))}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span className="grupo-chevron">{expandido ? '▼' : '▶'}</span>
                          <div>
                            <strong>{grupo.nombre}</strong>
                            {grupo.nit && <span style={{ color: '#94a3b8', marginLeft: 8, fontSize: '0.8rem' }}>NIT: {grupo.nit}</span>}
                          </div>
                          <span className="badge-pdfs" title="Eventos de pago">
                            📋 {eventos.length} {eventos.length === 1 ? 'pago' : 'pagos'}
                          </span>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ fontSize: '0.78rem', color: '#64748b' }}>{grupo.pagos.length} factura(s)</span>
                          <div style={{ fontWeight: 700, color: '#059669', fontSize: '1.05rem' }}>{formatMoney(grupo.total)}</div>
                        </div>
                      </div>
                      {expandido && (
                        <div className="historial-grupo-body">
                          <div className="eventos-lista">
                            {eventos.map((evento, ei) => {
                              const eventoKey = `${grupoKey}__${evento.key}`;
                              const eventoExpandido = !!eventosExpandidos[eventoKey];
                              const fechaLbl = evento.fecha_pago
                                ? new Date(evento.fecha_pago + 'T12:00:00').toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })
                                : '-';
                              return (
                                <div key={ei} className={`evento-pago ${eventoExpandido ? 'expandido' : ''}`}>
                                  <div className="evento-pago-header clickable" onClick={() => setEventosExpandidos(prev => ({ ...prev, [eventoKey]: !prev[eventoKey] }))}>
                                    <div className="evento-info">
                                      <span className="grupo-chevron">{eventoExpandido ? '▼' : '▶'}</span>
                                      <span className="evento-fecha">📅 {fechaLbl}</span>
                                      <span className="evento-total">{formatMoney(evento.total)}</span>
                                      <span className="evento-count">{evento.pagos.length} factura{evento.pagos.length !== 1 ? 's' : ''}</span>
                                      {evento.metodo_pago && <span className="evento-metodo">{evento.metodo_pago}</span>}
                                    </div>
                                    <div className="evento-acciones" onClick={(e) => e.stopPropagation()}>
                                      {evento.comprobante_ruta && (
                                        <button
                                          className="btn-ver-evidencia"
                                          onClick={() => window.open(`${UPLOAD_URL}/pago-pdf/${encodeURIComponent(evento.comprobante_ruta)}?token=${localStorage.getItem('tuvi_token')}`, '_blank')}
                                          title="Ver PDF del comprobante"
                                        >
                                          📄 Ver PDF
                                        </button>
                                      )}
                                      {!evento.comprobante_ruta && evento.comprobante_archivo && (
                                        <button
                                          className="btn-ver-evidencia"
                                          onClick={() => window.open(`${UPLOAD_URL}/pago-file/${encodeURIComponent(evento.comprobante_archivo)}?token=${localStorage.getItem('tuvi_token')}`, '_blank')}
                                          title="Ver comprobante"
                                        >
                                          📎 Comprobante
                                        </button>
                                      )}
                                      {evento.comprobante_ruta && proveedorId && (
                                        <button
                                          className="btn-enviar-proveedor"
                                          title="Enviar PDF al correo del proveedor"
                                          onClick={async () => {
                                            if (!window.confirm(`¿Enviar este comprobante por correo al proveedor ${grupo.nombre}?`)) return;
                                            try {
                                              const token = localStorage.getItem('tuvi_token');
                                              const res = await fetch(`${UPLOAD_URL}/enviar-comprobante-pago`, {
                                                method: 'POST',
                                                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                                body: JSON.stringify({
                                                  proveedor_id: proveedorId,
                                                  pdf_keys: [evento.comprobante_ruta],
                                                  total_pagado: evento.total,
                                                  fecha_pago: evento.fecha_pago,
                                                  metodo_pago: evento.metodo_pago,
                                                  referencia: evento.referencia,
                                                }),
                                              });
                                              const data = await res.json();
                                              if (data.success) alert('✅ ' + data.message);
                                              else alert('❌ ' + (data.message || 'Error al enviar'));
                                            } catch (err) {
                                              alert('❌ Error de conexión: ' + err.message);
                                            }
                                          }}
                                        >
                                          ✉️ Enviar
                                        </button>
                                      )}
                                      <button
                                        className="btn-editar-fecha-evento"
                                        title="Editar fecha de este pago"
                                        onClick={async () => {
                                          const fechaHoy = evento.fecha_pago || new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
                                          const nuevaFecha = window.prompt('Nueva fecha del pago (YYYY-MM-DD):', fechaHoy);
                                          if (!nuevaFecha) return;
                                          if (!/^\d{4}-\d{2}-\d{2}$/.test(nuevaFecha)) {
                                            error('Formato inválido. Use YYYY-MM-DD');
                                            return;
                                          }
                                          try {
                                            // Actualizar todos los pagos del evento
                                            for (const p of evento.pagos) {
                                              await client.mutate(
                                                `mutation($id: Int!, $fecha_pago: String!) { editarFechaPago(id: $id, fecha_pago: $fecha_pago) { success message } }`,
                                                { id: p.id, fecha_pago: nuevaFecha }
                                              );
                                            }
                                            success('Fecha actualizada');
                                            cargarHistorialPagos();
                                          } catch (err) {
                                            error('Error al actualizar: ' + err.message);
                                          }
                                        }}
                                      >
                                        ✏️
                                      </button>
                                    </div>
                                  </div>
                                  {eventoExpandido && (
                                    <div className="evento-pago-body">
                                      {evento.referencia && (
                                        <div className="evento-meta">
                                          <span className="label">Referencia:</span> <span className="value">{evento.referencia}</span>
                                          {evento.banco && <><span className="label" style={{ marginLeft: 14 }}>Banco:</span> <span className="value">{evento.banco}</span></>}
                                        </div>
                                      )}
                                      <div className="pagos-grid">
                                        {evento.pagos.map(pago => (
                                          <div key={pago.id} className="pago-card">
                                            <div className="pago-header">
                                              <span className="pago-monto">{formatMoney(pago.monto_pago)}</span>
                                            </div>
                                            <div className="pago-body">
                                              <div className="pago-factura">
                                                <span className="label">Factura:</span>
                                                <span className="value">#{pago.numero_factura}</span>
                                              </div>
                                              {pago.proyecto_nombre && (
                                                <div className="pago-proveedor">
                                                  <span className="label">Proyecto:</span>
                                                  <span className="value">{pago.proyecto_nombre}</span>
                                                </div>
                                              )}
                                              {pago.apartamento && (
                                                <div className="pago-metodo">
                                                  <span className="label">Apto:</span>
                                                  <span className="value">{pago.apartamento}</span>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Vista Retefuente DIAN */}
      {vista === 'retefuente' && (
        <div className="retefuente-view">
          <div className="retefuente-header-info">
            <div className="retefuente-stats-cards">
              <div className="retefuente-stat-card">
                <span className="rs-label">Facturas con Retefuente</span>
                <span className="rs-value">{retefuenteEstadisticas.total_facturas}</span>
              </div>
              <div className="retefuente-stat-card destacado">
                <span className="rs-label">Total a pagar a DIAN</span>
                <span className="rs-value">{formatMoney(retefuenteEstadisticas.total_retefuente)}</span>
              </div>
              <div className="retefuente-stat-card">
                <span className="rs-label">Proveedores</span>
                <span className="rs-value">{retefuenteEstadisticas.total_proveedores}</span>
              </div>
            </div>
            <div className="retefuente-actions-bar">
              <button
                className="btn-toggle-historial-ret"
                onClick={() => {
                  setMostrarHistorialRet(!mostrarHistorialRet);
                  if (!mostrarHistorialRet) cargarHistorialRetefuente();
                }}
              >
                {mostrarHistorialRet ? '📋 Ver Pendientes' : '📜 Ver Historial'}
              </button>
            </div>
          </div>

          {!mostrarHistorialRet && (
            <>
              <div className="filtros-section">
                <div className="filtros-row">
                  <div className="filtro-group busqueda">
                    <label>Buscar</label>
                    <input
                      type="text"
                      placeholder="# factura, proveedor, NIT..."
                      value={filtros.busqueda}
                      onChange={(e) => handleFiltroChange('busqueda', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="loading-state">Cargando retefuentes...</div>
              ) : retefuentesPendientes.length === 0 ? (
                <div className="empty-state">
                  <span>✅</span>
                  <p>No hay retefuentes pendientes de pago a DIAN</p>
                </div>
              ) : (
                <>
                  <div className="retefuente-toolbar">
                    <div className="retefuente-toolbar-left">
                      <label className="retefuente-select-all">
                        <input
                          type="checkbox"
                          checked={retefuentesSeleccionadas.length === retefuentesPendientes.length && retefuentesPendientes.length > 0}
                          onChange={seleccionarTodasRetefuentes}
                        />
                        Seleccionar todas ({retefuentesPendientes.length})
                      </label>
                      {retefuentesSeleccionadas.length > 0 && (
                        <span className="retefuente-seleccionadas-info">
                          <strong>{retefuentesSeleccionadas.length}</strong> seleccionadas · <strong>{formatMoney(totalRetefuenteSeleccionado)}</strong>
                        </span>
                      )}
                    </div>
                    <button
                      className="btn-pagar-retefuente"
                      disabled={retefuentesSeleccionadas.length === 0}
                      onClick={() => setMostrarModalRetefuente(true)}
                    >
                      💰 Pagar a DIAN
                    </button>
                  </div>

                  <div className="retefuente-tabla">
                    <table>
                      <thead>
                        <tr>
                          <th style={{ width: 40 }}></th>
                          <th>Factura</th>
                          <th>Proveedor</th>
                          <th>NIT</th>
                          <th>Proyecto</th>
                          <th>Fecha</th>
                          <th className="right">Ret. Fuente</th>
                          <th className="right">Ret. IVA</th>
                          <th className="right">Total a DIAN</th>
                        </tr>
                      </thead>
                      <tbody>
                        {retefuentesPendientes.map(f => {
                          const seleccionada = !!retefuentesSeleccionadas.find(r => r.id === f.id);
                          const totalDIAN = parseFloat(f.retencion_fuente || 0) + parseFloat(f.retencion_iva || 0);
                          return (
                            <tr key={f.id} className={seleccionada ? 'seleccionada' : ''}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={seleccionada}
                                  onChange={() => toggleSeleccionRetefuente(f)}
                                />
                              </td>
                              <td><strong>#{f.numero_factura}</strong></td>
                              <td>{f.proveedor_nombre}</td>
                              <td>{f.proveedor_nit || '-'}</td>
                              <td>
                                {f.proyecto_nombre ? (
                                  <span>{f.proyecto_nombre}{f.apartamento ? ` · ${f.apartamento}` : ''}</span>
                                ) : '-'}
                              </td>
                              <td>{formatDate(f.fecha_emision)}</td>
                              <td className="right">{f.retencion_fuente > 0 ? formatMoney(f.retencion_fuente) : '-'}</td>
                              <td className="right">{f.retencion_iva > 0 ? formatMoney(f.retencion_iva) : '-'}</td>
                              <td className="right monto-retefuente"><strong>{formatMoney(totalDIAN)}</strong></td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan="8" className="right"><strong>TOTAL PENDIENTE A DIAN:</strong></td>
                          <td className="right monto-total-retefuente">
                            <strong>{formatMoney(retefuenteEstadisticas.total_retefuente)}</strong>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </>
              )}
            </>
          )}

          {mostrarHistorialRet && (
            <div className="retefuente-historial">
              <div className="filtros-section">
                <div className="filtros-row">
                  <div className="filtro-group">
                    <label>Desde</label>
                    <input type="date" value={filtros.fecha_desde || ''} onChange={e => { handleFiltroChange('fecha_desde', e.target.value); cargarHistorialRetefuente(); }} />
                  </div>
                  <div className="filtro-group">
                    <label>Hasta</label>
                    <input type="date" value={filtros.fecha_hasta || ''} onChange={e => { handleFiltroChange('fecha_hasta', e.target.value); cargarHistorialRetefuente(); }} />
                  </div>
                </div>
              </div>
              {historialRetefuente.length === 0 ? (
                <div className="empty-state"><span>📜</span><p>No hay pagos de retefuente registrados</p></div>
              ) : (
                <div className="retefuente-historial-lista">
                  {historialRetefuente.map(p => (
                    <div key={p.id} className="retefuente-historial-item">
                      <div className="rh-header">
                        <div className="rh-fecha">
                          <strong>{formatDate(p.fecha_pago)}</strong>
                          {p.periodo && <span className="rh-periodo">Periodo: {p.periodo}</span>}
                        </div>
                        <div className="rh-monto">{formatMoney(p.monto_total)}</div>
                      </div>
                      <div className="rh-detalles">
                        <span>{p.total_facturas} factura(s)</span>
                        {p.metodo_pago && <span>· {p.metodo_pago}</span>}
                        {p.referencia && <span>· Ref: {p.referencia}</span>}
                        {p.banco && <span>· {p.banco}</span>}
                      </div>
                      {p.facturas?.length > 0 && (
                        <div className="rh-facturas">
                          {p.facturas.map(f => (
                            <div key={f.id} className="rh-factura-row">
                              <span>#{f.numero_factura} · {f.proveedor_nombre}</span>
                              <span className="rh-factura-monto">{formatMoney(f.retencion_fuente)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {p.notas && <div className="rh-notas">{p.notas}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modal Pagar Retefuente DIAN */}
      {mostrarModalRetefuente && (
        <div className="modal-overlay" onClick={() => !guardandoRetefuente && setMostrarModalRetefuente(false)}>
          <div className="modal-content modal-pagar-retefuente" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>💰 Pagar Retefuente a DIAN</h2>
              <button className="modal-close" onClick={() => setMostrarModalRetefuente(false)} disabled={guardandoRetefuente}>✕</button>
            </div>
            <div className="modal-body">
              <div className="ret-modal-resumen">
                <div className="ret-modal-resumen-linea">
                  <span>Facturas seleccionadas:</span>
                  <strong>{retefuentesSeleccionadas.length}</strong>
                </div>
                <div className="ret-modal-resumen-linea total">
                  <span>Total a pagar a DIAN:</span>
                  <strong>{formatMoney(totalRetefuenteSeleccionado)}</strong>
                </div>
              </div>

              <div className="ret-modal-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Fecha de Pago *</label>
                    <input
                      type="date"
                      value={formRetefuente.fecha_pago}
                      onChange={e => setFormRetefuente(prev => ({ ...prev, fecha_pago: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Periodo</label>
                    <input
                      type="text"
                      placeholder="Ej: 2026-05"
                      value={formRetefuente.periodo}
                      onChange={e => setFormRetefuente(prev => ({ ...prev, periodo: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Método de Pago</label>
                    <select
                      value={formRetefuente.metodo_pago}
                      onChange={e => setFormRetefuente(prev => ({ ...prev, metodo_pago: e.target.value }))}
                    >
                      <option value="transferencia">Transferencia</option>
                      <option value="pse">PSE</option>
                      <option value="cheque">Cheque</option>
                      <option value="efectivo">Efectivo</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Banco</label>
                    <input
                      type="text"
                      value={formRetefuente.banco}
                      onChange={e => setFormRetefuente(prev => ({ ...prev, banco: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group full">
                    <label>Referencia / N° Comprobante</label>
                    <input
                      type="text"
                      value={formRetefuente.referencia}
                      onChange={e => setFormRetefuente(prev => ({ ...prev, referencia: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group full">
                    <label>Notas</label>
                    <textarea
                      rows="2"
                      value={formRetefuente.notas}
                      onChange={e => setFormRetefuente(prev => ({ ...prev, notas: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              <div className="ret-modal-facturas">
                <h4>Facturas incluidas en este pago:</h4>
                <div className="ret-modal-facturas-lista">
                  {retefuentesSeleccionadas.map(f => {
                    const totalDIAN = parseFloat(f.retencion_fuente || 0) + parseFloat(f.retencion_iva || 0);
                    return (
                      <div key={f.id} className="ret-modal-factura-item">
                        <span>#{f.numero_factura} · {f.proveedor_nombre}</span>
                        <span className="monto-mini">{formatMoney(totalDIAN)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancelar" onClick={() => setMostrarModalRetefuente(false)} disabled={guardandoRetefuente}>
                Cancelar
              </button>
              <button className="btn-guardar" onClick={procesarPagoRetefuente} disabled={guardandoRetefuente}>
                {guardandoRetefuente ? 'Procesando...' : `Confirmar Pago ${formatMoney(totalRetefuenteSeleccionado)}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Registrar Pago */}
      <ModalRegistrarPago
        show={mostrarModalPago}
        onClose={() => {
          setMostrarModalPago(false);
          setFacturasSeleccionadas([]);
        }}
        facturas={facturasSeleccionadas}
        onPagoRegistrado={handlePagoRegistrado}
      />
    </div>
  );
};

export default Pagos;
